import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, Eye, MessageCircle } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import FinalCTA from "@/components/site/FinalCTA";
import { blogPosts } from "@/data/blogPosts";
import { fetchBlogMeta } from "@/data/blogApi";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function Blog() {
  const [featured, ...rest] = blogPosts;
  const [meta, setMeta] = useState({});
  useEffect(() => {
    fetchBlogMeta().then(setMeta);
  }, []);
  const stat = (slug) => meta[slug] || { views: 0, comments: 0 };
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="blog-page">
      <Seo
        title="The Happy Blog | Happy Tiddlers Swim School"
        description="Tips, stories and water safety advice from Happy Tiddlers Swim School in Barton-le-Clay — helping families raise confident, capable swimmers for life."
        path="/blog"
      />
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-14 pb-10 md:pt-20 md:pb-14" data-testid="blog-header">
          <div className="blob absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#0EA5E9]/10" />
          <div className="container mx-auto px-6 md:px-12 relative">
            <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">The Happy Blog</span>
            <h1 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-[1.05] max-w-3xl">
              Stories, tips & water safety
            </h1>
            <p className="mt-6 text-lg text-[#334155] leading-relaxed max-w-2xl">
              Advice, insight and stories from our team — helping families raise confident,
              capable swimmers who love and respect the water.
            </p>
          </div>
        </section>

        <section className="pb-24 md:pb-32">
          <div className="container mx-auto px-6 md:px-12">
            {/* Featured */}
            <Link to={`/blog/${featured.slug}`} data-testid="blog-featured">
              <motion.article
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="group grid lg:grid-cols-2 gap-8 lg:gap-12 items-center bg-white rounded-[2rem] p-6 md:p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500"
              >
                <div className="rounded-[1.5rem] overflow-hidden">
                  <img src={featured.heroImage} alt={featured.heroAlt} className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div>
                  <span className="inline-block text-xs font-bold uppercase tracking-widest bg-[#0EA5E9]/10 text-[#0EA5E9] px-3 py-1.5 rounded-full">{featured.category}</span>
                  <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A] leading-tight group-hover:text-[#0EA5E9] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-[#334155] leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-[#64748B]" data-testid={`blog-stats-${featured.slug}`}>
                    <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} /> {fmtDate(featured.date)}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {featured.readTime}</span>
                    <span className="inline-flex items-center gap-1.5"><Eye size={15} /> {stat(featured.slug).views} views</span>
                    <span className="inline-flex items-center gap-1.5"><MessageCircle size={15} /> {stat(featured.slug).comments} comments</span>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 font-bold text-[#FF7A1A] group-hover:gap-3 transition-all">
                    Read the story <ArrowRight size={18} />
                  </span>
                </div>
              </motion.article>
            </Link>

            {/* Rest */}
            {rest.length > 0 && (
              <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {rest.map((post) => (
                  <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}>
                    <article className="group bg-white rounded-[1.75rem] overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 h-full">
                      <div className="overflow-hidden">
                        <img src={post.heroImage} alt={post.heroAlt} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-6">
                        <span className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9]">{post.category}</span>
                        <h3 className="mt-2 font-heading text-lg font-bold text-[#0F172A] leading-snug group-hover:text-[#0EA5E9] transition-colors">{post.title}</h3>
                        <p className="mt-2 text-sm text-[#64748B] leading-relaxed line-clamp-3">{post.excerpt}</p>
                        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-[#94A3B8]" data-testid={`blog-stats-${post.slug}`}>
                          <span className="inline-flex items-center gap-1"><Clock size={13} /> {post.readTime}</span>
                          <span className="inline-flex items-center gap-1"><Eye size={13} /> {stat(post.slug).views}</span>
                          <span className="inline-flex items-center gap-1"><MessageCircle size={13} /> {stat(post.slug).comments}</span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
